import { formatPropertyAddress } from '../utils/propertyAddress';
import './MobileMapDisclosure.css';
import MapPlaceLayers from './MapPlaceLayers';
import {addIllustratedBasemap} from '../services/illustratedBasemap';
import { propertyFetch } from '../services/idxPropertyClient';
import { loadPropertyMedia, propertyMediaUrls } from '../services/propertyMedia';
import { rememberPropertyPhoto } from '../services/propertyThumbnails';
import { sendEmailRequest } from '../services/emailDeliveryService';
import { usePropertySave } from './PropertySaveProvider';
import RobustSearchControls from './RobustSearchControls';
import useListSearchFilters from '../utils/useListSearchFilters';
import { searchFromQuery, searchQuery } from '../utils/propertySearchFilters';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { SITE_CONFIG } from '../config';
import PropertyThumbnail from './PropertyThumbnail';
import { decryptPassword } from '../utils/decrypt';
import { warmPropertyBatch } from '../utils/warmPropertyDetail';
import PropertyDetailModal from './PropertyDetailModal';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PropertyListView.css';

const PropertyListView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const initialListFilters = location.state?.searchFilters || searchFromQuery(location.search);

  const [properties, setProperties] = useState(() => location.state?.properties || []);
  const [listViewPage, setListViewPage] = useState(0);
  const [propertyDetail, setPropertyDetail] = useState(null);
  const [propertyDetailImages, setPropertyDetailImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const [likedProperties, setLikedProperties] = useState(new Set());
  const { favorites, toggleFavorite } = usePropertySave();
  useEffect(() => { setLikedProperties?.(favorites); }, [favorites]);
  const [showLogin, setShowLogin] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

    const [expandedMap, setExpandedMap] = useState(null);
  const [placesExpandedMapInstance,setPlacesExpandedMapInstance]=useState(null);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [enlargedImageIndex, setEnlargedImageIndex] = useState(0);
  const [showAiStaging, setShowAiStaging] = useState(false);
  const [isAdmin] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem('user')); return u?.roles?.includes('ROLE_ADMIN') || u?.roles?.includes('ROLE_ASSISTANT') || false; } catch { return false; }
  });
  const [showEmailSendModal, setShowEmailSendModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailCustomerName, setEmailCustomerName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailGenerating, setEmailGenerating] = useState(false);
  const [emailMode, setEmailMode] = useState('email');
  const [socialCopy, setSocialCopy] = useState(null);
  const [selectedListings, setSelectedListings] = useState(new Set());
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailRecipient, setBulkEmailRecipient] = useState('');
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailCustomerName, setBulkEmailCustomerName] = useState('');
  const [bulkEmailPreview, setBulkEmailPreview] = useState('');
  const [bulkEmailGenerating, setBulkEmailGenerating] = useState(false);
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPassword, setSenderPassword] = useState('');

  const listSearch = useListSearchFilters({
    initialFilters: location.state?.searchFilters || searchFromQuery(location.search),
    initialProperties: location.state?.properties,
    quickEdits: {},
    defaultState: SITE_CONFIG.SEARCH_STATES || SITE_CONFIG.STATE,
    siteId: SITE_CONFIG.SITE_ID, onResults: setProperties, onPageReset: setListViewPage, pageSize:9
  });
  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const res = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, { headers: { 'X-API-Key': 'treas-api-key-2025' } });
        if (res.ok) {
          const data = await res.json();
          if (data.senderEmail) setSenderEmail(data.senderEmail);
          if (data.senderEmailPassword) decryptPassword(data.senderEmailPassword).then(pw => { if (pw) setSenderPassword(pw); });
        }
      } catch (e) {}
    };
    fetchFlags();
  }, []);

  const openEnlargedImage = (url, index) => {
    setEnlargedImage(url);
    setEnlargedImageIndex(index);
  };

  const closeEnlargedImage = () => {
    setEnlargedImage(null);
    setStagingImage(null);
  };
  const [stagingImage, setStagingImage] = useState(null);
  const [stagingLoading, setStagingLoading] = useState(false);

  const handleAiStaging = async (type) => {
    setStagingLoading(true);
    try {
      const promptResponse = await fetch(`https://tophamtechbeb.com/api/stage/v1/${type}-prompts/random`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (!promptResponse.ok) throw new Error('Failed to get staging prompt');

      const promptData = await promptResponse.json();
      const formData = new FormData();
      formData.append('image_url', enlargedImage);
      formData.append('design_prompt', promptData.prompt);

      const stagingResponse = await fetch('https://tophamtechbeb.com/api/stage/v1/stage-room', {
        method: 'POST',
        headers: { 'X-API-Key': 'treas-api-key-2025' },
        body: formData
      });

      if (!stagingResponse.ok) throw new Error('Failed to stage room');

      const stagedBlob = await stagingResponse.blob();
      const imageUrl = URL.createObjectURL(stagedBlob);
      setStagingImage(imageUrl);
      setShowAiStaging(false);
    } catch (error) {
      console.error('Error staging room:', error);
      alert('Failed to stage room. Please try again.');
    } finally {
      setStagingLoading(false);
    }
  };

  const fetchPropertyDetail = async (propertyId, summaryData) => {
    const requestId = ++detailRequestRef.current;
    const cover = summaryData?.first_photo_url || summaryData?.image_url || summaryData?.photo_url;
    setPropertyDetail(summaryData || null);
    setPropertyDetailImages(cover ? [cover] : []);
    setImagesLoading(!cover);
    try {
      const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-data/${propertyId}`, {});
      if (requestId !== detailRequestRef.current) return;
      if (response.ok) {
        const data = await response.json();
        const property = data.properties[0];
        setPropertyDetail({
          ...property,
          created_at: property.created_at || summaryData?.created_at,
          updated_at: property.updated_at || summaryData?.updated_at
        });
        if (property.listing_key) {
          fetchAllPropertyImages(property.listing_key, requestId, cover);
        }
      } else {
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
      if (requestId === detailRequestRef.current) setImagesLoading(false);
    }
  };

  const fetchAllPropertyImages = async (listingKey, requestId, cover) => {
    try {
      const images = propertyMediaUrls(await loadPropertyMedia(listingKey));
      if (requestId !== detailRequestRef.current) return;
      if (images.length > 0) {
          const firstThree = images.slice(0, 3);
          setPropertyDetailImages(firstThree);
          setImagesLoading(false);
          rememberPropertyPhoto(listingKey, images[0]);
          if (images.length > 3) {
            setTimeout(() => {
              if (requestId === detailRequestRef.current) setPropertyDetailImages(images);
            }, 500);
          }
      } else {
        setPropertyDetailImages(cover ? [cover] : []);
        setImagesLoading(false);
      }
    } catch (error) {
      console.error('Error fetching all property images:', error);
      if (requestId === detailRequestRef.current) {
        setPropertyDetailImages(cover ? [cover] : []);
        setImagesLoading(false);
      }
    }
  };

  const closePropertyDetail = () => {
    detailRequestRef.current += 1;
    setPropertyDetail(null);
    setImagesLoading(false);
  };

  useEffect(() => {
    if (location.state?.properties) {
      setProperties(location.state.properties);
    }
  }, [location.state]);

  const user = location.state?.user;

  // Preload summary image files for the current and next page without another IDX request.
  useEffect(() => {
    if (!properties.length) return;
    const perPage = 9;
    const start = listViewPage * perPage;
    warmPropertyBatch(properties.slice(start, start + perPage), undefined, { media: false });
    warmPropertyBatch(properties.slice(start + perPage, start + perPage * 2));
  }, [properties, listViewPage]);

  const getTileCoords = (lat, lon, zoom) => {
    const x = Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
    const y = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
    return { x, y };
  };

  useEffect(() => {
    if (expandedMap && mapRef.current) {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
      }

      const map = L.map(mapRef.current,{minZoom:1,maxZoom:19}).setView([expandedMap.lat, expandedMap.lon], 15);

      addIllustratedBasemap(map);

      const houseIcon = L.divIcon({
        html: '🏠',
        className: 'house-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });

      L.marker([expandedMap.lat, expandedMap.lon], { icon: houseIcon }).addTo(map);

      leafletMapRef.current = map;
      setPlacesExpandedMapInstance(map);
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        setPlacesExpandedMapInstance(null);
      }
    };
  }, [expandedMap]);

  return (
    <div className={`property-list-view ${isDarkMode ? 'dark-mode' : ''}`}>
      <div style={{padding:'12px 20px'}}><RobustSearchControls filters={listSearch.filters} onChange={listSearch.setFilters} scope={{state:listSearch.filters.state || SITE_CONFIG.SEARCH_STATES || SITE_CONFIG.STATE, propertyType:listSearch.filters.propertyType || 'Residential'}} />
      {listSearch.loading && <p role="status">Loading matching homes…</p>}
      {listSearch.error && <p role="alert">{listSearch.error}</p>}</div>

      <div className="list-view-navigation">
        <button type="button" className="back-to-map-btn"
          onClick={() => {if(location.state?.isSavedHomes && location.state?.savedMap){sessionStorage.setItem('globeMapData',JSON.stringify(location.state.savedMap));navigate('/search?saved=true');}else{const params=searchQuery(listSearch.filters);params.set('view','map');navigate(`/search?${params}`);}}}>
          🗺 Map View
        </button>
        <button type="button" className="back-to-map-btn"
          onClick={() => {const params=searchQuery(listSearch.filters);params.set('view','filters');navigate(`/search?${params}`);}}>
          ← Search Filters
        </button>
      </div>

      <div className="list-view-container">
        {properties.slice(listViewPage * 9, (listViewPage + 1) * 9).map((property, index) => {
          return (
            <div
              key={index}
              className="list-view-card"
              onClick={() => fetchPropertyDetail(property.id, property)}
            >
              {isAdmin && (
                <div
                  className="list-view-select"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedListings(prev => {
                      const next = new Set(prev);
                      if (next.has(property.listing_key)) next.delete(property.listing_key);
                      else if (next.size < 3) next.add(property.listing_key);
                      return next;
                    });
                  }}
                >
                  <input type="checkbox" checked={selectedListings.has(property.listing_key)} readOnly />
                </div>
              )}
              <div className="list-view-hero">
                <PropertyThumbnail
                  listingKey={property.listing_key}
                  src={property.first_photo_url || property.image_url || property.photo_url}
                  alt={`Property at ${property.address || property.street_address}`}
                  className="list-view-image"
                />
                <div className="list-view-overlay">
                  <div className="list-view-price">
                    ${property.list_price?.toLocaleString() || property.current_price?.toLocaleString() || property.price}
                  </div>
                  <div className="list-view-address">
                    {formatPropertyAddress(property)}
                  </div>
                  <div className="list-view-meta">
                    {property.bedrooms_total && <span>🛏️ {property.bedrooms_total} bed</span>}
                    {property.property_type && <span>{property.property_type}</span>}
                  </div>
                </div>
                {property.latitude && property.longitude && (
                  <div
                    className="list-view-map"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedMap({lat: property.latitude, lon: property.longitude, address: property.address});
                    }}
                  >
                    <img
                      src={`https://tile.openstreetmap.org/15/${getTileCoords(property.latitude, property.longitude, 15).x}/${getTileCoords(property.latitude, property.longitude, 15).y}.png`}
                      alt={`Map location for ${property.address || property.street_address}`}
                    />
                    <div className="list-view-house-icon">🏠</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="pagination-controls">
          {listSearch.restartSearch && <button onClick={()=>listSearch.restart()}>Restart search</button>}
          {listViewPage > 0 && (
            <button
              disabled={listSearch.loading} onClick={() => setListViewPage(listViewPage - 1)}
              className="pagination-btn"
            >
              ← Previous
            </button>
          )}
          <span className="pagination-info">
            Page {listViewPage + 1}
          </span>
          {(listSearch.hasMore || (listViewPage + 1) * 9 < properties.length) && (
            <button
              disabled={listSearch.loading} onClick={() => listSearch.nextPage(listViewPage)}
              className="pagination-btn"
            >
              Next →
            </button>
          )}
        </div>
        <div className="mobile-map-disclosure" style={{gridColumn: '1 / -1', background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', borderRadius: '8px'}}>
          <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} style={{height: '20px', objectFit: 'contain'}} />
          <span style={{fontSize: '10px', color: '#ccc', flex: 1}}>{SITE_CONFIG.MLS.DISCLAIMER} | Brokerage License: {SITE_CONFIG.AGENT.BROKERAGE_LICENSE} | Agent License: {SITE_CONFIG.AGENT.LICENSE}</span>
        </div>
      </div>

      {isAdmin && selectedListings.size > 0 && (
        <div className="bulk-email-bar">
          <span>{selectedListings.size}/3 selected</span>
          <button onClick={() => { setBulkEmailSubject('Featured Properties'); setBulkEmailPreview(''); setShowBulkEmailModal(true); }}>
            ✉️ Email Selected
          </button>
          <button onClick={() => setSelectedListings(new Set())}>Clear</button>
        </div>
      )}

      {showBulkEmailModal && (
        <div className="modal-overlay" onClick={() => setShowBulkEmailModal(false)}>
          <div className="email-send-modal" style={{position: 'relative', margin: 'auto', top: '20vh'}} onClick={(e) => e.stopPropagation()}>
            <div className="email-send-modal-content">
              <button className="email-send-modal-close" onClick={() => setShowBulkEmailModal(false)}>×</button>
              <h3>Email {selectedListings.size} Properties</h3>
              {bulkEmailPreview === 'sent' ? (
                <div style={{textAlign: 'center', padding: '30px 10px'}}>
                  <div style={{fontSize: '40px', marginBottom: '10px'}}>✅</div>
                  <p style={{color: '#28a745', fontWeight: 600, margin: 0}}>Email sent successfully!</p>
                </div>
              ) : !bulkEmailPreview ? (
                <div className="email-send-form">
                  <input type="email" placeholder="Recipient email *" value={bulkEmailRecipient} onChange={(e) => setBulkEmailRecipient(e.target.value)} />
                  <input type="text" placeholder="Customer name (optional)" value={bulkEmailCustomerName} onChange={(e) => setBulkEmailCustomerName(e.target.value)} />
                  <input type="text" placeholder="Subject *" value={bulkEmailSubject} onChange={(e) => setBulkEmailSubject(e.target.value)} />
                  <button
                    disabled={!bulkEmailRecipient.trim() || !bulkEmailSubject.trim() || bulkEmailGenerating}
                    onClick={async () => {
                      setBulkEmailGenerating(true);
                      try {
                        const res = await fetch('https://tophamtechbe.com/api/treas/comm/property-search-preview?format=html', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                          body: JSON.stringify({
                            customerName: bulkEmailCustomerName || undefined,
                            agentName: SITE_CONFIG.AGENT.NAME,
                            siteId: SITE_CONFIG.SITE_ID,
                            listingBaseUrl: `https://www.${SITE_CONFIG.BASE_URL}`,
                            searchFilters: { listingKey: [...selectedListings].join(',') }
                          })
                        });
                        const data = await res.json();
                        if (data.html) setBulkEmailPreview(data.html);
                        else alert(data.message || 'No preview generated');
                      } catch (err) {
                        alert('Error generating preview');
                      }
                      setBulkEmailGenerating(false);
                    }}
                  >
                    {bulkEmailGenerating ? 'Generating...' : 'Preview Email'}
                  </button>
                </div>
              ) : (
                <div className="email-send-preview">
                  <iframe title="Email Preview" srcDoc={bulkEmailPreview} style={{width: '100%', height: '300px', border: '1px solid #ddd', borderRadius: '4px'}} />
                  <div className="email-send-actions">
                    <button onClick={() => setBulkEmailPreview('')}>← Back</button>
                    <button
                      disabled={bulkEmailSending}
                      onClick={async () => {
                        setBulkEmailSending(true);
                        try {
                          const formData = new FormData();
                          formData.append('emailaddress', bulkEmailRecipient);
                          formData.append('title', bulkEmailSubject);
                          formData.append('body', bulkEmailPreview);
                          if (senderEmail) formData.append('email', senderEmail);
                          if (senderPassword) formData.append('emailPassword', senderPassword);
                          const res = await sendEmailRequest('https://tophamtechbe.com/api/treas/comm/send-email', {
                            method: 'POST',
                            headers: { 'X-API-Key': 'treas-api-key-2025' },
                            body: formData
                          });
                          if (res.ok) {
                            setBulkEmailPreview('sent');
                            setTimeout(() => { setShowBulkEmailModal(false); setSelectedListings(new Set()); }, 2000);
                          } else alert('Failed to send email');
                        } catch (err) {
                          alert('Error sending email');
                        }
                        setBulkEmailSending(false);
                      }}
                    >
                      {bulkEmailSending ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <PropertyDetailModal
        propertyDetail={propertyDetail}
        propertyDetailImages={propertyDetailImages}
        imagesLoading={imagesLoading}
        onClose={closePropertyDetail}
        user={user}
        isAdmin={isAdmin}
        likedProperties={likedProperties}
        setLikedProperties={setLikedProperties}
        senderEmail={senderEmail}
        senderPassword={senderPassword}
      />
      {expandedMap && (
        <div className="modal-overlay" onClick={() => setExpandedMap(null)}>
          <div className="expanded-map" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setExpandedMap(null)}>×</button>
            <div ref={mapRef} className="leaflet-map"></div>
              <MapPlaceLayers map={placesExpandedMapInstance} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyListView;
